import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { apiCall } from '../utils/api';

interface Gig {
  id: number;
  name: string;
  venue: string | null;
  status: string;
}

const photos = [
  { src: '/images/photo-1.png', alt: 'Crowd with arms raised as a singer performs with the live band' },
  { src: '/images/photo-2.png', alt: 'Audience member belting out a country song on stage' },
  { src: '/images/photo-3.png', alt: 'Singer from the crowd performing with the full band' },
  { src: '/images/photo-4.png', alt: 'Friends cheering on their mate performing on stage' },
  { src: '/images/photo-5.png', alt: 'Friends browsing the song list on their phone' },
  { src: '/images/photo-6.png', alt: 'Two friends duetting together on stage with the band' },
];

const HomePage: React.FC = () => {
  const [activeGig, setActiveGig] = useState<Gig | null>(null);
  const [loading, setLoading] = useState(true);
  const [email, setEmail] = useState('');
  const [emailStatus, setEmailStatus] = useState<'idle' | 'submitting' | 'success' | 'error'>('idle');

  useEffect(() => {
    const checkGig = async () => {
      try {
        const res = await apiCall('/api/songs/gigs/current');
        if (res.ok) {
          setActiveGig(await res.json());
        } else {
          setActiveGig(null);
        }
      } catch {
        setActiveGig(null);
      } finally {
        setLoading(false);
      }
    };

    checkGig();
    const interval = setInterval(checkGig, 30000);
    return () => clearInterval(interval);
  }, []);

  const handleEmailSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email) return;

    setEmailStatus('submitting');
    try {
      const res = await apiCall('/api/newsletter/subscribe', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email }),
      });

      if (!res.ok) throw new Error('Failed to subscribe');
      setEmailStatus('success');
      setEmail('');
    } catch {
      setEmailStatus('error');
    }
  };

  return (
    <main className="crowd-site">
      <section className="marketing-hero">
        <div className="marketing-hero__media" aria-hidden="true">
          <img src="/images/photo-1.png" alt="" />
        </div>
        <div className="marketing-hero__content">
          <img className="marketing-hero__logo" src="/images/rodeoke-logo.png" alt="Rodeoke" />
          <p className="eyebrow">Live band country karaoke</p>
          <h1>Step up. Pick a country hit. Front the band.</h1>
          <p className="marketing-hero__copy">
            Rodeoke puts you on stage with real musicians, a cheering crowd, and a song list built for big voices and bigger moments.
          </p>
          <div className="hero-actions">
            {loading ? (
              <span className="button-primary button-primary--muted">Checking the stage</span>
            ) : activeGig ? (
              <Link className="button-primary" to="/gig">
                Enter the gig
              </Link>
            ) : (
              <a className="button-primary" href="#events">
                See how it works
              </a>
            )}
            <Link className="button-secondary" to="/gig/catalogue">
              Browse songs
            </Link>
          </div>
          {activeGig && (
            <div className="live-ribbon">
              <span className="live-dot" />
              {activeGig.name} is live{activeGig.venue ? ` at ${activeGig.venue}` : ''}
            </div>
          )}
        </div>
      </section>

      <section className="split-band" id="events">
        <div>
          <p className="eyebrow">For the crowd</p>
          <h2>No karaoke machine. No awkward guessing. Just a live band waiting for your song.</h2>
        </div>
        <div className="feature-grid">
          <article className="feature-card">
            <span className="feature-card__mark">01</span>
            <h3>Find your song</h3>
            <p>Search the catalogue from your phone and choose from country crowd-pleasers ready for the band.</p>
          </article>
          <article className="feature-card">
            <span className="feature-card__mark">02</span>
            <h3>Join the queue</h3>
            <p>Add your name, keep an eye on now and next, and get ready when your turn comes up.</p>
          </article>
          <article className="feature-card">
            <span className="feature-card__mark">03</span>
            <h3>Own the room</h3>
            <p>Grab the mic, sing with the band, then let your friends rate the performance afterward.</p>
          </article>
        </div>
      </section>

      <section className="photo-strip" aria-label="Rodeoke in action">
        {photos.slice(0, 4).map(photo => (
          <img key={photo.src} src={photo.src} alt={photo.alt} />
        ))}
      </section>

      <section className="video-band">
        <div className="video-band__text">
          <p className="eyebrow">The vibe</p>
          <h2>People sing harder when the room is with them.</h2>
          <p>
            Rodeoke is built for phones in the crowd and quick decisions at the table: pick, queue, cheer, repeat.
          </p>
        </div>
        <div className="video-frame">
          <iframe
            src="https://www.youtube.com/embed/R6rT1ZgLUZ8"
            title="Rodeoke Live Performance"
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
            allowFullScreen
          />
        </div>
      </section>

      <section className="signup-band">
        <div>
          <p className="eyebrow">Next show</p>
          <h2>Get the next Rodeoke date.</h2>
          <p>We will only use this to send Rodeoke event updates.</p>
        </div>

        {emailStatus === 'success' ? (
          <div className="success-panel">You are on the list. See you down front.</div>
        ) : (
          <form className="signup-form" onSubmit={handleEmailSubmit}>
            <input
              type="email"
              value={email}
              onChange={e => setEmail(e.target.value)}
              placeholder="Email address"
              required
            />
            <button type="submit" disabled={emailStatus === 'submitting'}>
              {emailStatus === 'submitting' ? 'Sending...' : 'Notify me'}
            </button>
            {emailStatus === 'error' && <p className="form-error">Something went wrong. Please try again.</p>}
          </form>
        )}
      </section>

      <footer className="site-footer">
        <span>Rodeoke</span>
        <div className="site-footer__links">
          <a href="https://www.instagram.com/rodeoke/" target="_blank" rel="noreferrer">
            Instagram
          </a>
          <Link to="/gig/login">Band login</Link>
        </div>
      </footer>
    </main>
  );
};

export default HomePage;
